/** Top-up dialog (UX review C1) — replaces window.prompt for payments.
 *
 * Preset amounts + inline validation + explicit currency so the customer
 * always sees exactly what they're paying before a PaymentIntent is created.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { formatCurrency } from "../../utils/format";

const PRESETS = [10, 25, 50, 100];

export interface TopUpDialogProps {
  open: boolean;
  onClose: () => void;
  /** Creates the payment session; resolves with payment_intent_id. */
  onCreateIntent: (amount: number) => Promise<string>;
}

export default function TopUpDialog({ open, onClose, onCreateIntent }: TopUpDialogProps) {
  const [amount, setAmount] = useState("25.00");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setAmount("25.00");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const numericAmount = useMemo(() => {
    const n = parseFloat(amount);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
  }, [amount]);

  const valid =
    Number.isFinite(numericAmount) &&
    numericAmount >= 1 &&
    numericAmount <= 10000;

  const handleConfirm = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await onCreateIntent(numericAmount);
      onClose();
    } catch (err: any) {
      setError(err?.message || err?.response?.data?.detail || "Top-up unavailable");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AccountBalanceWalletIcon color="primary" />
        Add credit
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose or enter an amount (USD). You'll complete payment securely via
          Stripe.
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }} role="group" aria-label="Preset amounts">
          {PRESETS.map((preset) => (
            <Chip
              key={preset}
              label={formatCurrency(preset, 0)}
              clickable
              color={numericAmount === preset ? "primary" : "default"}
              onClick={() => setAmount(preset.toFixed(2))}
              aria-pressed={numericAmount === preset}
            />
          ))}
        </Box>

        <TextField
          label="Amount (USD)"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={amount !== "" && !valid}
          helperText={
            amount !== "" && !valid
              ? "Enter an amount between $1 and $10,000"
              : "Minimum $1.00 · charged in US dollars"
          }
          inputProps={{
            inputMode: "decimal",
            "aria-label": "Top-up amount in US dollars",
          }}
          fullWidth
          size="small"
          disabled={submitting}
        />

        {valid && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            Balance top-up: <strong>{formatCurrency(numericAmount)}</strong>
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!valid || submitting}
        >
          {submitting ? "Creating payment…" : "Continue to payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
